import React, { useState, useEffect } from 'react';

const LegalNoticePopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has already agreed to the notice
    const hasAgreed = localStorage.getItem('legal_notice_agreed');
    if (!hasAgreed) {
      setIsOpen(true);
    }
  }, []);

  const handleAgree = () => {
    // Store agreement in localStorage
    localStorage.setItem('legal_notice_agreed', 'true');
    localStorage.setItem('legal_notice_agreed_date', new Date().toISOString());
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:p-6">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden animate-fade-in mx-2 sm:mx-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-accent-600 to-accent-700 p-4 sm:p-6 text-white flex-shrink-0">
          <h2 className="text-lg sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Important Legal Notice
          </h2>
          <p className="mt-1 sm:mt-2 text-white/90 text-xs sm:text-sm">Please read carefully before proceeding</p>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="space-y-3 sm:space-y-4">
            {/* Supreme Court Order */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-base sm:text-lg text-yellow-900 mb-1 sm:mb-2 flex items-center gap-2">
                <span className="text-lg sm:text-2xl">⚖️</span>
                Supreme Court Guidelines (2018)
              </h3>
              <p className="text-yellow-800 text-sm sm:text-base">
                In compliance with the 2018 Supreme Court order, direct online sales of firecrackers are restricted. 
                We operate as an enquiry-based platform where you can browse products and submit your requirements.
              </p>
            </div>

            {/* How It Works */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-base sm:text-lg text-blue-900 mb-1 sm:mb-2 flex items-center gap-2">
                <span className="text-lg sm:text-2xl">🛒</span>
                How Our Enquiry System Works
              </h3>
              <ol className="list-decimal list-inside space-y-1 sm:space-y-2 text-blue-800 text-sm sm:text-base">
                <li>Browse and add your desired firecrackers to the cart</li>
                <li>Submit your enquiry with delivery details</li>
                <li>Our team will contact you within 24 hours</li>
                <li>We'll confirm availability and delivery</li>
              </ol>
            </div>

            {/* License and Compliance */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-base sm:text-lg text-green-900 mb-1 sm:mb-2 flex items-center gap-2">
                <span className="text-lg sm:text-2xl">✅</span>
                100% Legal & Licensed Operation
              </h3>
              <ul className="space-y-1 sm:space-y-2 text-green-800 text-sm sm:text-base">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="sm:hidden">Licensed under Explosives Act</span>
                  <span className="hidden sm:inline">Licensed under Explosives Act (License No: E5/17678/2020)</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="sm:hidden">Safety compliant shops & warehouses</span>
                  <span className="hidden sm:inline">All shops and warehouses comply with explosive safety regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="sm:hidden">Legal transport & delivery</span>
                  <span className="hidden sm:inline">Delivery through registered and legal transport providers</span>
                </li>
                <li className="hidden sm:flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Following all statutory compliances as per government regulations</span>
                </li>
              </ul>
            </div>

            {/* Customer Commitment */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-base sm:text-lg text-purple-900 mb-1 sm:mb-2 flex items-center gap-2">
                <span className="text-lg sm:text-2xl">🎆</span>
                Our Commitment to You
              </h3>
              <p className="text-purple-800 text-sm sm:text-base">
                We value our customers and respect the law. This enquiry system ensures you can safely browse and order firecrackers 
                while we maintain full legal compliance. Celebrate your festivals with joy and complete peace of mind!
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex-shrink-0">
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            By clicking "I Agree & Continue", you acknowledge our enquiry-based ordering process.
          </p>
          <button
            onClick={handleAgree}
            className="w-full bg-accent-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold hover:bg-accent-700 transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl text-sm sm:text-base"
          >
            I Agree & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalNoticePopup;